import { useMemo } from "react";
import { getMaturityColor } from "@/utils/maturity-colors";

type MaturityGaugeProps = {
  score: number;
  maxScore?: number;
};

const SIZE = 120;
const STROKE_WIDTH = 10;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const START_ANGLE = 135;
const ARC_DEGREES = 270;

function MaturityGauge({ score, maxScore = 2.5 }: MaturityGaugeProps) {
  const { dashOffset, color, displayScore } = useMemo(() => {
    const clampedScore = Math.min(Math.max(score, 0), maxScore);
    const percentage = maxScore > 0 ? clampedScore / maxScore : 0;
    const arcLength = (ARC_DEGREES / 360) * CIRCUMFERENCE;
    const offset = arcLength - percentage * arcLength;

    return {
      dashOffset: offset,
      color: getMaturityColor(score),
      displayScore: clampedScore.toFixed(1).replace(".", ","),
    };
  }, [score, maxScore]);

  const arcLength = (ARC_DEGREES / 360) * CIRCUMFERENCE;
  const center = SIZE / 2;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: SIZE, height: SIZE }}
    >
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        className="transform"
      >
        <circle
          cx={center}
          cy={center}
          r={RADIUS}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${arcLength} ${CIRCUMFERENCE}`}
          transform={`rotate(${START_ANGLE} ${center} ${center})`}
        />
        <circle
          cx={center}
          cy={center}
          r={RADIUS}
          fill="none"
          stroke={color}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeDasharray={`${arcLength} ${CIRCUMFERENCE}`}
          strokeDashoffset={dashOffset}
          transform={`rotate(${START_ANGLE} ${center} ${center})`}
          className="transition-all duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold" style={{ color }}>
          {displayScore}
        </span>
        <span className="text-[10px] text-slate-400 -mt-0.5">
          / {maxScore.toFixed(1).replace(".", ",")}
        </span>
      </div>
    </div>
  );
}

export default MaturityGauge;
